import { createFormContext } from '@mantine/form';
import { z } from 'zod';

import { TenantLoginProviderType, Salutation, UserState, GroupType } from 'api/v3/models';
import { i18n } from 'i18n';
import { IRowData } from 'shared/components/extendedDataTable/interfaces';
import { userPhoneTextRegex, userProfileTextRegex } from 'shared/userFieldRegex';

const optionalProfileText = z
    .string()
    .regex(userProfileTextRegex, { message: i18n.t('invalidProfileText') })
    .nullish();

const optionalPhoneText = z
    .string()
    .regex(userPhoneTextRegex, { message: i18n.t('invalidPhoneNumber') })
    .nullish();

export const basicUserInformation = z.object({
    id: z.string(),
    firstName: z.string().nullish(),
    lastName: z.string().nullish(),
    userName: z.string().nullish(),
    email: z.string().nullish(),
    displayName: z.string().nullish(),
    profilePictureUri: z.string().nullish(),
    state: z.enum(UserState),
    loginProviderType: z.enum(TenantLoginProviderType).nullish(),
});

export const detailUserInformation = basicUserInformation.extend({
    salutation: z.enum(Salutation),
    title: z.string().nullish(),
    mobileNumber: z.string().nullish(),
    mobileNumberCountryCode: z.string().nullish(),
    phoneNumber: z.string().nullish(),
    phoneNumberCountryCode: z.string().nullish(),
    language: z.string().nullish(),
    tenant: z.object({
        id: z.string(),
        name: z.string(),
    }),
    firstLoginDate: z.date().nullish(),
    lastLoginDate: z.date().nullish(),
    lastUsedClient: z.string().nullish(),
});

export const userRoles = z
    .object({
        id: z.string().nullish(),
        roleId: z.string(),
        apiResourceId: z.string(),
        name: z.string(),
        displayName: z.string().nullish(),
        description: z.string().nullish(),
    })
    .array();

export const userGroups = z
    .object({
        id: z.string().nullish(),
        groupId: z.string(),
        name: z.string(),
        groupType: z.enum(GroupType),
        description: z.string().nullish(),
    })
    .array();

export const basicUserFormSchema = z.object({
    basicInformation: basicUserInformation,
    roles: userRoles,
    groups: userGroups,
});

export const detailUserFormSchema = z.object({
    basicInformation: detailUserInformation,
    roles: userRoles,
    groups: userGroups,
});

export const basicUserFormValidationSchema = basicUserFormSchema.extend({
    basicInformation: basicUserInformation.extend({
        firstName: optionalProfileText,
        lastName: optionalProfileText,
    }),
});

export const detailUserFormValidationSchema = detailUserFormSchema.extend({
    basicInformation: detailUserInformation.extend({
        firstName: optionalProfileText,
        lastName: optionalProfileText,
        title: optionalProfileText,
        phoneNumber: optionalPhoneText,
        mobileNumber: optionalPhoneText,
    }),
});

export type BasicUserFormValues = z.infer<typeof basicUserFormSchema>;
export type DetailUserFormValues = z.infer<typeof detailUserFormSchema>;

export type DetailUserFormType = Omit<DetailUserFormValues, 'roles' | 'groups'> & {
    roles: (DetailUserFormValues['roles'][0] & IRowData)[];
    groups: (DetailUserFormValues['groups'][0] & IRowData)[];
};

export type BasicUserFormType = Omit<BasicUserFormValues, 'roles' | 'groups'> & {
    roles: (DetailUserFormValues['roles'][0] & IRowData)[];
    groups: (DetailUserFormValues['groups'][0] & IRowData)[];
};

export type DetailUserFormRolesFormType = DetailUserFormType['roles'][0];
export type DetailUserDataGridRolesType = DetailUserFormRolesFormType & IRowData;

export type DetailUserFormGroupsFormType = DetailUserFormType['groups'][0];
export type DetailUserDataGridGroupsType = DetailUserFormGroupsFormType & IRowData;

export const isBasicUser = (obj: unknown): obj is BasicUserFormType => {
    return basicUserFormSchema.safeParse(obj).success;
};

export const isDetailUser = (obj: unknown): obj is DetailUserFormType => {
    return detailUserFormSchema.safeParse(obj).success;
};

// You can give context variables any name
export const [DetailUserFormProvider, useDetailUserFormContext, useDetailUserForm] =
    createFormContext<DetailUserFormType>();
export const [BasicUserFormProvider, useBasicUserFormContext, useBasicUserForm] =
    createFormContext<BasicUserFormType>();
